import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PlusCircle, MinusCircle, Megaphone, Loader2, Users } from "lucide-react";

/** Invitación lista para enviar por Telegram a todos los usuarios (el botón lleva a la web). */
const inviteText = () =>
  ["👥 ÚNETE A LA COMUNIDAD",
   "",
   "Ya tienes el editor; ahora entra también a la comunidad privada:",
   "• Muro con publicaciones, comentarios, reacciones e historias del día",
   "• Chats privados y canales, con llamadas de voz y video 1 a 1",
   "• Mercado para vender y comprar entre miembros",
   "",
   "Pulsa el botón de abajo para entrar.",
   "",
   "Crea tu cuenta con tu correo y, en «Mi cuenta», pulsa «Vincular Telegram»",
   "para tener un solo saldo en la web y en el bot."].join("\n");

const Tile = ({ icon: Icon, label, hint, color, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className="flex-1 text-left p-4 rounded-lg border transition-colors hover:border-red-500/60"
    style={{ backgroundColor: "#111827", borderColor: "#1E293B" }}>
    <div className="flex items-center gap-2 text-slate-100 font-semibold text-sm">
      <Icon className="w-4 h-4" style={{ color }} /> {label}
    </div>
    <div className="text-slate-500 text-xs mt-1">{hint}</div>
  </button>
);

export const AdminQuickActions = () => {
  const [mode, setMode] = useState(null); // add | deduct | broadcast
  const [tid, setTid] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => { setMode(null); setBusy(false); };

  const submitCredits = async () => {
    const amt = parseInt(amount, 10);
    if (!tid.trim()) return toast.error("Escribe el ID de Telegram del usuario");
    if (!amt || amt <= 0) return toast.error("Cantidad inválida");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${tid.trim()}/credits`,
        { action: mode, amount: amt, reason: reason || undefined });
      toast.success(`Saldo actualizado: ${data.before} → ${data.after} créditos`);
      setTid(""); setAmount(""); setReason("");
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar el saldo");
      setBusy(false);
    }
  };

  const [audience, setAudience] = useState(null);

  useEffect(() => {
    if (mode !== "broadcast") return;
    setAudience(null);
    api.get("/admin/broadcast/audience").then(({ data }) => setAudience(data)).catch(() => {});
  }, [mode]);

  const submitBroadcast = async () => {
    if (message.trim().length < 2) return toast.error("Escribe el anuncio");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", { message, only_authorized: true });
      toast.success(`Anuncio enviado a ${data.sent} usuario(s)${data.failed ? `, ${data.failed} fallaron` : ""}`);
      setMessage("");
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el anuncio");
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3" data-testid="admin-quick-actions">
        <Tile testid="quick-add-credits" icon={PlusCircle} color="#34D399"
          label="Añadir créditos" hint="Carga créditos a un usuario por su ID"
          onClick={() => setMode("add")} />
        <Tile testid="quick-deduct-credits" icon={MinusCircle} color="#F87171"
          label="Quitar créditos" hint="Descuenta créditos de un usuario"
          onClick={() => setMode("deduct")} />
        <Tile testid="quick-broadcast" icon={Megaphone} color="#F59E0B"
          label="Lanzar anuncio" hint="Mensaje por Telegram a todos los usuarios"
          onClick={() => setMode("broadcast")} />
        <Tile testid="quick-invite-community" icon={Users} color="#60A5FA"
          label="Invitar a la comunidad" hint="Invitación por Telegram a todos los usuarios"
          onClick={() => { setMessage(inviteText()); setMode("broadcast"); }} />
      </div>

      <Dialog open={mode === "add" || mode === "deduct"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-[#0D111A] border-[#1E293B] text-slate-100" data-testid="credits-dialog">
          <DialogHeader>
            <DialogTitle>{mode === "add" ? "Añadir créditos" : "Quitar créditos"}</DialogTitle>
            <DialogDescription className="text-slate-500">
              Indica el ID de Telegram del usuario y la cantidad de créditos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">ID de Telegram del usuario</label>
              <Input data-testid="quick-credits-id" value={tid} onChange={(e) => setTid(e.target.value)}
                placeholder="6731591552" className="bg-[#0B0F19] border-[#1E293B] text-slate-100" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Cantidad de créditos</label>
              <Input data-testid="quick-credits-amount" type="number" min={1} value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="100"
                className="bg-[#0B0F19] border-[#1E293B] text-slate-100" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Motivo (opcional)</label>
              <Input data-testid="quick-credits-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Transferencia recibida" className="bg-[#0B0F19] border-[#1E293B] text-slate-100" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}
              className="border-[#334155] bg-transparent text-slate-200">Cancelar</Button>
            <Button data-testid="quick-credits-submit" onClick={submitCredits} disabled={busy}
              className={mode === "add" ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-rose-600 hover:bg-rose-500 text-white"}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "add" ? "Añadir" : "Quitar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mode === "broadcast"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-[#0D111A] border-[#1E293B] text-slate-100" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle>Lanzar anuncio a todos</DialogTitle>
            <DialogDescription className="text-slate-500">
              El mensaje se envía por Telegram a todos los usuarios autorizados.
            </DialogDescription>
          </DialogHeader>
          <textarea data-testid="broadcast-message" rows={5} value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ej. Nuevo: ya puedes mandarme notas de voz y editar PDFs de varias páginas."
            className="w-full bg-[#0B0F19] border border-[#1E293B] text-slate-100 text-sm rounded-md px-3 py-2 resize-none" />
          <div className="text-[11px] text-slate-500" data-testid="broadcast-audience">
            {audience
              ? `Se enviará a ${audience.subscribed} suscriptor(es) de ${audience.total} usuario(s). ${audience.opted_out} desactivaron los anuncios con /offspam.`
              : "Calculando destinatarios…"}
            <br />
            Los usuarios activan los anuncios con <span className="text-slate-300">/spam</span> y los desactivan con <span className="text-slate-300">/offspam</span>.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}
              className="border-[#334155] bg-transparent text-slate-200">Cancelar</Button>
            <Button data-testid="broadcast-submit" onClick={submitBroadcast} disabled={busy}
              className="bg-amber-600 hover:bg-amber-500 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar a todos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminQuickActions;
